/**
 * AtlasFederalModule — THE FEDERAL DOCKET (society wave, 2026-09-19).
 *
 * One window, a tab per civilization: who holds the eight federal offices, the
 * purse and its rates, the bill before the Assembly with the blocs' last vote,
 * the works, the Tribunal's cases, the Speaker's promises. Everything is a
 * render of replicated rows — the client computes no tally and sets no number.
 *
 * A player's whole write surface is three reducers:
 *   · a RESIDENT the town knows registers a preference on the open bill
 *     (`castFederalPreference` — it weighs eight ballots in THEIR town's bloc);
 *   · a player city's mayor stands the city with a civilization and casts its
 *     bloc (`declareCityAllegiance`, `castCityFederalVote`).
 * The buttons only gate for UX; the server re-checks residency, regard, the
 * mayor's seat and the bill's status.
 *
 * Opened by F11-free means: the menu bar's Assembly entry emits
 * `atlas:open-federal` (never a synthesized key), and `__federal.open()`.
 */
import type { App, IModule } from 'enginex';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { makeWindow, type UiWindow } from '../lib/ui_window.js';
import { settlementSites } from '../../../shared/settlements.js';
import { civilizationOf } from '../lib/biome_settlements.js';
import { settlementNamesFor } from '../lib/settlement_names.js';
import { FEDERAL_OFFICES, FEDERAL_OFFICE_LABEL, FEDERAL_WORKS, FED_PREF_MIN_REGARD, LEGITIMACY_FLOOR, playerCityVotes, BUDGET_LABEL, isBudgetSplit, type BudgetSplit } from '../lib/federal_core.js';

const CIV_LABEL: Record<string, string> = { a: 'Aurelian Concord', b: 'Borean Compact' };
const FED_TABLES = [
  'SELECT * FROM federal_state', 'SELECT * FROM federal_office', 'SELECT * FROM federal_bill', 'SELECT * FROM federal_bloc_vote',
  'SELECT * FROM federal_member', 'SELECT * FROM federal_promise', 'SELECT * FROM tribunal_case', 'SELECT * FROM federal_work',
  'SELECT * FROM contingent_request', 'SELECT * FROM war_bond', 'SELECT * FROM city_allegiance',
  'SELECT * FROM federal_pref WHERE voter = :sender', 'SELECT * FROM federal_city_vote',
];

type Row = Record<string, unknown>;
interface FedDb {
  federalState: { iter(): Iterable<Row> }; federalOffice: { iter(): Iterable<Row> }; federalBill: { iter(): Iterable<Row> };
  federalBlocVote: { iter(): Iterable<Row> }; federalMember: { iter(): Iterable<Row> }; federalPromise: { iter(): Iterable<Row> };
  tribunalCase: { iter(): Iterable<Row> }; federalWork: { iter(): Iterable<Row> }; contingentRequest: { iter(): Iterable<Row> };
  warBond: { iter(): Iterable<Row> }; cityAllegiance: { iter(): Iterable<Row> }; federalPref: { iter(): Iterable<Row> }; federalCityVote: { iter(): Iterable<Row> };
  residency: { iter(): Iterable<Row> }; playerStanding: { iter(): Iterable<Row> }; city: { iter(): Iterable<Row> };
  /** The budget stance (added after the first cut: optional, so an older publish still renders). */
  federalBudget?: { iter(): Iterable<Row> };
}

const el = (tag: string, css: string, text?: string): HTMLElement => { const e = document.createElement(tag); e.style.cssText = css; if (text !== undefined) e.textContent = text; return e; };
const pctBps = (bps: unknown): string => `${(Number(bps) / 100).toFixed(1)} %`;

/** How a bill's value reads for its kind (pure — the harness pins it). */
export function billValueLabel(kind: string, value: number): string {
  switch (kind) {
    case 'levy': case 'customs': return pctBps(value);
    case 'relief': return `${value} measures of grain`;
    case 'work': return FEDERAL_WORKS[value]?.label ?? `work ${value}`;
    case 'war': return 'declare';
    case 'peace': return 'sue for peace';
    default: return 'tomorrow';
  }
}

export class AtlasFederalModule implements IModule {
  readonly name = 'AtlasFederal';
  private win: UiWindow | null = null;
  private body: HTMLDivElement | null = null;
  private civ = 'a';
  private subbed = false;
  private timer = 0;
  private status = '';
  /** Town names by site id, per commons seed (the maps' own table; DB settlementId = site.id + 1). */
  private siteNames: { seed: string; names: Map<number, string> } | null = null;

  private townName(settlementId: unknown): string {
    const sid = Number(settlementId);
    const conn = atlas.conn;
    let seed = '';
    if (conn) for (const w of conn.db.world.iter()) if (w.kind === 'commons') { seed = w.terrainSeed; break; }
    if (seed && (!this.siteNames || this.siteNames.seed !== seed)) {
      try { this.siteNames = { seed, names: settlementNamesFor(settlementSites(seed), (ss) => civilizationOf(ss)) }; } catch { this.siteNames = null; }
    }
    return this.siteNames?.names.get(sid - 1) ?? `settlement ${sid}`;
  }

  init(_app: App): void {
    void _app;
    bus.on('atlas:open-federal', () => this.open());
    bus.on('atlas:world-cleared', () => this.close());
    (window as unknown as Record<string, unknown>).__federal = {
      open: () => this.open(), close: () => this.close(), isOpen: () => this.isOpen(),
      civ: (c: string) => { if (c === 'a' || c === 'b') { this.civ = c; this.render(); } },
      digest: () => this.digest(),
    };
  }

  isOpen(): boolean { return !!this.win && this.win.isOpen(); }
  private close(): void { window.clearInterval(this.timer); this.timer = 0; this.win?.setOpen(false); }

  private db(): FedDb | null { return (atlas.conn?.db as unknown as FedDb | undefined) ?? null; }
  private myHex(): string { return atlas.identityHex ?? ''; }

  private open(): void {
    if (!this.win) {
      this.win = makeWindow({
        id: 'federal', title: 'The Assembly', icon: 'people', width: 760, height: 600, minWidth: 480, minHeight: 320,
        x: Math.max(12, (window.innerWidth - 760) / 2), y: 84, onClose: () => { window.clearInterval(this.timer); this.timer = 0; },
      });
      this.win.root.id = 'federal-window';
      this.body = el('div', 'padding:10px 14px 16px;font:12px system-ui;color:#cfe0f5') as HTMLDivElement;
      this.win.body.appendChild(this.body);
    }
    this.win.setOpen(true);
    if (!this.subbed && atlas.conn) {
      this.subbed = true;
      try { atlas.conn.subscriptionBuilder().onApplied(() => this.render()).subscribe(FED_TABLES); } catch { this.subbed = false; }
      // Its OWN builder: a rejected query (a server published before the table existed) must not sink the batch above.
      try { atlas.conn.subscriptionBuilder().onApplied(() => this.render()).subscribe(['SELECT * FROM federal_budget']); } catch { /* older publish */ }
    }
    // The civilization my residency sits under opens first.
    const db = this.db(); const me = this.myHex();
    if (db && me) {
      for (const r of db.residency.iter()) {
        if ((r.identity as { toHexString(): string }).toHexString() !== me) continue;
        for (const m of db.federalMember.iter()) if (m.settlementId === r.settlementId) { this.civ = String(m.civ); break; }
      }
    }
    this.render();
    window.clearInterval(this.timer);
    this.timer = window.setInterval(() => { if (this.isOpen()) this.render(); }, 4000);
  }

  /** What the window would show, as data (probes read this; the DOM is its render). */
  digest(): Record<string, unknown> {
    const db = this.db();
    if (!db) return { ready: false };
    const out: Record<string, unknown> = { ready: true, civ: this.civ };
    for (const fs of db.federalState.iter()) {
      const civ = String(fs.civ);
      out[civ] = {
        speaker: fs.speakerName, purse: Number(fs.treasury), levyBps: fs.levyBps, customsBps: fs.customsBps, legitimacy: fs.legitimacy,
        offices: [...db.federalOffice.iter()].filter((o) => o.civ === civ).length,
        bills: [...db.federalBill.iter()].filter((b) => b.civ === civ).map((b) => `${b.kind}:${b.status}:${b.source}`),
        members: [...db.federalMember.iter()].filter((m) => m.civ === civ).length,
      };
    }
    return out;
  }

  private call(name: string, args: Record<string, unknown>, done: string): void {
    const red = atlas.conn?.reducers as unknown as Record<string, ((a: Record<string, unknown>) => Promise<void>) | undefined> | undefined;
    const fn = red?.[name];
    if (!fn) { this.status = 'Not connected.'; this.render(); return; }
    this.status = '…';
    fn.call(red, args).then(() => { this.status = done; this.render(); })
      .catch((e: unknown) => { this.status = String((e as Error)?.message ?? e); this.render(); });
  }

  private render(): void {
    const db = this.db(); const body = this.body;
    if (!body) return;
    body.textContent = '';
    if (!db) { body.textContent = 'Not connected.'; return; }

    // ── tabs ──
    const tabs = el('div', 'display:flex;gap:6px;margin-bottom:10px');
    for (const c of ['a', 'b']) {
      const b = el('button', `cursor:pointer;padding:5px 12px;border-radius:6px;font:600 11px ui-monospace,monospace;border:1px solid ${c === this.civ ? '#d9a441' : '#22344c'};background:${c === this.civ ? '#2a2216' : '#141b28'};color:${c === this.civ ? '#f0d9a0' : '#9fb4cc'}`, CIV_LABEL[c]);
      b.dataset.fedCiv = c;
      b.addEventListener('click', () => { this.civ = c; this.render(); });
      tabs.appendChild(b);
    }
    body.appendChild(tabs);

    const fs = [...db.federalState.iter()].find((r) => r.civ === this.civ);
    if (!fs) { body.appendChild(el('div', 'color:#8fa3bd', 'This federation has not sat yet — its first session comes with the next four-hour policy epoch.')); return; }
    const members = [...db.federalMember.iter()].filter((m) => m.civ === this.civ);
    const nameOfSid = (sid: unknown): string => this.townName(sid);

    // ── head ──
    const head = el('div', 'display:grid;grid-template-columns:repeat(auto-fit,minmax(150px,1fr));gap:8px;margin-bottom:12px');
    const stat = (label: string, value: string, hint = ''): void => {
      const c = el('div', 'background:#101826;border:1px solid #1f2f45;border-radius:8px;padding:7px 9px');
      c.appendChild(el('div', 'font:600 10px ui-monospace,monospace;color:#7e93ad;text-transform:uppercase;letter-spacing:.04em', label));
      c.appendChild(el('div', 'font:600 14px system-ui;color:#e8f0fa;margin-top:2px', value));
      if (hint) c.title = hint;
      head.appendChild(c);
    };
    const legit = Number(fs.legitimacy);
    stat('Speaker', String(fs.speakerName || '(vacant)'));
    stat('Purse', `${Number(fs.treasury)} coins`, 'levy + customs in; salaries, relief grain, works and bonds out — every coin is a transfer');
    stat('Levy · customs', `${pctBps(fs.levyBps)} · ${pctBps(fs.customsBps)}`, 'the levy never exceeds 10 % of a member\'s INFLOW');
    const budget = [...(db.federalBudget?.iter() ?? [])].find((b) => b.civ === this.civ);
    if (budget && isBudgetSplit(String(budget.split))) {
      stat('The purse leans', `${BUDGET_LABEL[String(budget.split) as BudgetSplit].split(' — ')[0]}${budget.source === 'llm' ? ' · the Speaker\'s choice' : ' · by the rules'}`,
        `${BUDGET_LABEL[String(budget.split) as BudgetSplit]}. Salaries are paid before any of it.${budget.say ? ` "${String(budget.say)}"` : ''}`);
    }
    stat('Legitimacy', `${Math.round(legit * 100)} %${legit < LEGITIMACY_FLOOR ? ' — relief only' : ''}`, 'the share of bloc votes that elected the Speaker, less every broken promise');
    stat('Members', `${members.length} (${members.filter((m) => m.suspended === true).length} in arrears)`, 'no levy, no vote');
    stat('Reserve grain', `${Number(fs.reserveGrain).toFixed(0)}`);
    stat('War', fs.atWarWith ? `with ${CIV_LABEL[String(fs.atWarWith)] ?? fs.atWarWith}` : 'at peace');
    body.appendChild(head);

    const section = (title: string): HTMLElement => { body.appendChild(el('div', 'font:700 11px ui-monospace,monospace;color:#d9a441;letter-spacing:.06em;margin:12px 0 6px', title.toUpperCase())); const box = el('div', ''); body.appendChild(box); return box; };
    const line = (box: HTMLElement, left: string, right = '', dim = false): HTMLElement => {
      const r = el('div', `display:flex;justify-content:space-between;gap:12px;padding:3px 0;border-bottom:1px solid #16202e;color:${dim ? '#7e93ad' : '#cfe0f5'}`);
      r.appendChild(el('span', '', left)); r.appendChild(el('span', 'color:#9fb4cc;text-align:right', right));
      box.appendChild(r); return r;
    };

    // ── the bill before the Assembly ──
    const bills = [...db.federalBill.iter()].filter((b) => b.civ === this.civ).sort((x, y) => (BigInt(String(y.billId)) > BigInt(String(x.billId)) ? 1 : -1));
    const billBox = section('Before the Assembly');
    const open = bills.find((b) => b.status === 'open');
    if (!open) billBox.appendChild(el('div', 'color:#8fa3bd', 'No bill is open. The Speaker tables one at the next session.'));
    else {
      const card = el('div', 'background:#16130c;border:1px solid #4a3a1c;border-radius:8px;padding:9px 11px');
      card.id = 'federal-open-bill';
      card.appendChild(el('div', 'font:600 13px system-ui;color:#f3e6c4', String(open.text)));
      card.appendChild(el('div', 'color:#b9a67c;margin-top:3px;font-size:11px',
        `${String(open.kind)} → ${billValueLabel(String(open.kind), Number(open.value))} · round ${Number(open.round)} · chosen by ${open.source === 'llm' ? 'the Speaker (model)' : 'the clerks (rules)'} · passes by a double majority: of votes AND of people`));
      // A resident's preference
      const me = this.myHex();
      const res = me ? [...db.residency.iter()].find((r) => (r.identity as { toHexString(): string }).toHexString() === me) : undefined;
      const member = res ? members.find((m) => m.settlementId === res.settlementId) : undefined;
      let regard = 0;
      if (res) for (const s of db.playerStanding.iter()) if ((s.identity as { toHexString(): string }).toHexString() === me && s.settlementId === res.settlementId) { regard = Number(s.regard); break; }
      const mine = [...db.federalPref.iter()].find((p) => p.billId === open.billId);
      const row = el('div', 'display:flex;gap:8px;align-items:center;margin-top:8px;flex-wrap:wrap');
      const can = !!member && regard >= FED_PREF_MIN_REGARD;
      for (const [aye, label] of [[true, 'Aye'], [false, 'Nay']] as Array<[boolean, string]>) {
        const b = el('button', `cursor:${can ? 'pointer' : 'not-allowed'};padding:4px 14px;border-radius:6px;border:1px solid #3a4f6a;background:${mine && mine.aye === aye ? '#24405c' : '#141b28'};color:#dfe9f5;opacity:${can ? 1 : 0.5};font:600 11px system-ui`, label) as HTMLButtonElement;
        b.disabled = !can; b.dataset.fedPref = String(aye);
        b.addEventListener('click', () => this.call('castFederalPreference', { billId: open.billId, aye }, `Your preference (${label}) will weigh in your town's bloc.`));
        row.appendChild(b);
      }
      if (mine) {
        // A preference is a voice, not a vow: it can be taken back while the bill is open.
        const w = el('button', 'cursor:pointer;padding:4px 10px;border-radius:6px;border:1px solid #3a4f6a;background:#141b28;color:#9fb4cc;font:600 11px system-ui', 'Withdraw') as HTMLButtonElement;
        w.dataset.fedPref = 'withdraw';
        w.addEventListener('click', () => this.call('withdrawFederalPreference', { billId: open.billId }, 'Your preference is withdrawn.'));
        row.appendChild(w);
      }
      row.appendChild(el('span', 'color:#8fa3bd;font-size:11px', !res ? 'Only a resident of a member settlement may weigh in.' : !member ? 'Your town does not sit in this Assembly.'
        : regard < FED_PREF_MIN_REGARD ? `Your town does not know you well enough yet (regard ${regard} of ${FED_PREF_MIN_REGARD}).` : 'Your voice weighs eight ballots in your town\'s stance — the bloc still votes as one.'));
      card.appendChild(row);
      // A player city's bloc
      for (const c of db.city.iter()) {
        if ((c.mayor as { toHexString(): string }).toHexString() !== me || c.status === 'invalid') continue;
        const al = [...db.cityAllegiance.iter()].find((a) => a.cityId === c.cityId);
        const cr = el('div', 'display:flex;gap:8px;align-items:center;margin-top:6px;flex-wrap:wrap;color:#9fb4cc;font-size:11px');
        cr.appendChild(el('span', '', `${String(c.name)} (${playerCityVotes(Number(c.cityRank))} votes):`));
        if (!al || al.civ !== this.civ) {
          const b = el('button', 'cursor:pointer;padding:3px 10px;border-radius:6px;border:1px solid #3a4f6a;background:#141b28;color:#dfe9f5;font:600 11px system-ui', `Stand with the ${CIV_LABEL[this.civ]}`);
          b.addEventListener('click', () => this.call('declareCityAllegiance', { cityId: c.cityId, civ: this.civ }, 'The city has declared its allegiance.'));
          cr.appendChild(b);
        } else for (const [aye, label] of [[true, 'City votes Aye'], [false, 'City votes Nay']] as Array<[boolean, string]>) {
          const b = el('button', 'cursor:pointer;padding:3px 10px;border-radius:6px;border:1px solid #3a4f6a;background:#141b28;color:#dfe9f5;font:600 11px system-ui', label);
          b.addEventListener('click', () => this.call('castCityFederalVote', { cityId: c.cityId, billId: open.billId, aye }, 'The city\'s bloc is cast.'));
          cr.appendChild(b);
        }
        card.appendChild(cr);
      }
      billBox.appendChild(card);
    }
    if (this.status) billBox.appendChild(el('div', 'color:#e9c46a;margin-top:6px;font-size:11px', this.status));

    // ── the docket ──
    const past = bills.filter((b) => b.status !== 'open').slice(0, 8);
    if (past.length > 0) {
      const box = section('The docket');
      for (const b of past) {
        const verdict = b.status === 'passed' ? 'PASSED' : b.status === 'mediated' ? 'sent to mediation' : 'voted down';
        line(box, `${String(b.text)}`, `${verdict} · ${Number(b.yesVotes)}–${Number(b.noVotes)} votes · ${Number(b.yesPop)}–${Number(b.noPop)} souls · ${b.source === 'llm' ? 'model' : 'rules'}`, b.status !== 'passed');
      }
    }

    // ── offices ──
    const offBox = section('Offices');
    const offices = [...db.federalOffice.iter()].filter((o) => o.civ === this.civ);
    for (const o of FEDERAL_OFFICES) {
      const h = offices.find((x) => x.office === o);
      line(offBox, FEDERAL_OFFICE_LABEL[o], h ? `${String(h.holderName)} · ${nameOfSid(h.settlementId)}` : '(vacant)', !h);
    }

    // ── works, promises, tribunal, war ──
    const works = [...db.federalWork.iter()].filter((w) => w.civ === this.civ);
    if (works.length > 0) {
      const box = section('Federal works');
      for (const w of works) line(box, FEDERAL_WORKS.find((d) => d.key === w.kind)?.label ?? String(w.kind), w.status === 'done' ? 'finished' : `${Math.round(Number(w.progress))} / ${Math.round(Number(w.target))}`);
    }
    const promises = [...db.federalPromise.iter()].filter((p) => p.civ === this.civ);
    if (promises.length > 0) {
      const box = section('The Speaker\'s promises');
      for (const p of promises.slice(-6)) line(box, `${p.kind === 'work' ? 'a federal work' : 'relief'} for ${nameOfSid(p.settlementId)}`, String(p.status), p.status !== 'open');
    }
    const cases = [...db.tribunalCase.iter()].filter((c) => c.civ === this.civ);
    if (cases.length > 0) {
      const box = section('The Tribunal');
      for (const c of cases.slice(-8).reverse()) {
        const st = c.status === 'proposed' ? 'cooling — no war vote may be held' : c.status === 'accepted' ? 'both mayors accepted' : c.status === 'refused' ? 'refused' : String(c.status);
        line(box, `${nameOfSid(c.aSid)} v. ${nameOfSid(c.bSid)} — ${Number(c.award)} coins offered`, `${st} · ${c.source === 'llm' ? 'model' : 'rules'}`, c.status !== 'proposed');
      }
    }
    const asks = [...db.contingentRequest.iter()].filter((r) => r.civ === this.civ);
    if (asks.length > 0) {
      const box = section('The Marshal\'s requests');
      const asked = asks.reduce((s, r) => s + Number(r.asked), 0), sent = asks.reduce((s, r) => s + Number(r.sent), 0);
      line(box, `${asks.length} towns asked for ${asked} soldiers`, `${sent} sent — each mayor decided`);
    }
    const bonds = [...db.warBond.iter()].filter((b) => b.civ === this.civ && b.status === 'open');
    if (bonds.length > 0) line(section('War bonds'), `${bonds.length} open`, `${bonds.reduce((s, b) => s + Number(b.principal), 0)} coins owed to merchants`);
  }

  dispose(): void { window.clearInterval(this.timer); this.win?.root.remove(); this.win = null; }
}
